'use client';
import React, { useEffect, useRef, useState } from 'react';

interface ScoreGaugeProps {
  score: number;
  total: number;
  passmark: number;
}

export default function ScoreGauge({ score, total, passmark }: ScoreGaugeProps) {
  const percentage = Math.round((score / total) * 100);
  const passed = score >= passmark;
  const circumference = 2 * Math.PI * 70;
  const [animatedOffset, setAnimatedOffset] = useState(circumference);
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    const targetOffset = circumference - (percentage / 100) * circumference;
    const timer = setTimeout(() => {
      setAnimatedOffset(targetOffset);
    }, 300);

    // Animate score counter
    let start = 0;
    const duration = 1200;
    const step = (timestamp: number, startTime: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.round(eased * score);
      setDisplayScore(start);
      if (progress < 1) {
        requestAnimationFrame((ts) => step(ts, startTime));
      }
    };
    const rafTimer = setTimeout(() => {
      requestAnimationFrame((ts) => step(ts, ts));
    }, 400);

    return () => {
      clearTimeout(timer);
      clearTimeout(rafTimer);
    };
  }, [score, percentage, circumference]);

  const strokeColor = passed ? '#10B981' : percentage >= 60 ? '#F59E0B' : '#EF4444';
  const glowColor = passed ? 'rgba(16,185,129,0.3)' : percentage >= 60 ? 'rgba(245,158,11,0.3)' : 'rgba(239,68,68,0.3)';

  return (
    <div className="flex flex-col items-center gap-4" aria-label={`Score: ${score} out of ${total}, ${percentage}%`}>
      <div className="relative">
        <svg
          width="200"
          height="200"
          viewBox="0 0 200 200"
          className="transform -rotate-90"
          aria-hidden="true"
        >
          {/* Background ring */}
          <circle
            cx="100"
            cy="100"
            r="70"
            fill="none"
            stroke="rgba(30,41,59,0.8)"
            strokeWidth="12"
          />
          {/* Passmark indicator arc */}
          <circle
            cx="100"
            cy="100"
            r="70"
            fill="none"
            stroke="rgba(245,158,11,0.25)"
            strokeWidth="12"
            strokeDasharray={`${(passmark / total) * circumference} ${circumference}`}
            strokeLinecap="round"
          />
          {/* Score arc */}
          <circle
            cx="100"
            cy="100"
            r="70"
            fill="none"
            stroke={strokeColor}
            strokeWidth="12"
            strokeDasharray={circumference}
            strokeDashoffset={animatedOffset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: `drop-shadow(0 0 8px ${glowColor})`,
            }}
          />
          {/* Passmark tick */}
          <circle
            cx={100 + 70 * Math.cos((passmark / total) * 2 * Math.PI - Math.PI / 2)}
            cy={100 + 70 * Math.sin((passmark / total) * 2 * Math.PI - Math.PI / 2)}
            r="5"
            fill="#F59E0B"
            className="transform rotate-90"
          />
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="font-mono-nums font-bold leading-none"
            style={{ fontSize: '3rem', color: strokeColor }}
            aria-hidden="true"
          >
            {displayScore}
          </span>
          <span className="text-sm text-muted-foreground font-medium mt-1" aria-hidden="true">
            out of {total}
          </span>
          <span
            className="text-xs font-semibold mt-1 px-2 py-0.5 rounded-full"
            style={{
              background: passed ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
              color: passed ? '#34D399' : '#F87171',
            }}
            aria-hidden="true"
          >
            {percentage}%
          </span>
        </div>
      </div>

      {/* Pass/fail label */}
      <div
        className="flex items-center gap-2 px-5 py-2.5 rounded-full"
        style={{
          background: passed ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${passed ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
        }}
        role="status"
        aria-live="polite"
      >
        {passed ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="rgba(16,185,129,0.2)" stroke="#10B981" strokeWidth="1.5" />
            <path d="M5 8l2.5 2.5 4-4" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="rgba(239,68,68,0.15)" stroke="#EF4444" strokeWidth="1.5" />
            <path d="M8 5v4M8 11v.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        )}
        <span className={`text-sm font-semibold ${passed ? 'text-success-light' : 'text-error-light'}`}>
          {passed ? `Pass — DVSA standard met` : `Below DVSA pass mark (${passmark}/${total})`}
        </span>
      </div>
    </div>
  );
}